/**
 * KAN-200 Ship A — tiny open/close bus for the read-only dialog. A guarded
 * button calls `openOfficeLockDialog()`; ONE `CompanyReadOnlyDialogHost` per
 * role home (AdminHomeOverlays, BillingHomeOverlays) listens and renders the
 * dialog, so no button owns a modal and the dialog never mounts twice.
 *
 * Follow-up: `reportCompanyLockRefusal()` is called by the shared fetch
 * observer (src/supabase.js) when the SERVER refused a write for a locked
 * company. Repeats inside the debounce window collapse into one report. The
 * bus does NOT open the dialog itself: the host re-reads the access level and
 * opens only when the company really is locked, so a suspended company gets
 * the suspended wording and a transient server failure on a paying company
 * shows nothing (security review, 2026-10-15). No host (worker, supervisor,
 * platform) → harmless no-op.
 */
import { stageModalHandoff } from '../ui/modalHandoff';

/** Shared by the dialog (receiver) and `handOffToCompanyLockDialog` (sender). */
export const COMPANY_LOCK_HANDOFF_KEY = 'company-lock';

const listeners = new Set();
const refusalListeners = new Set();
export const LOCK_REFUSAL_DEBOUNCE_MS = 2000;
let lastRefusalAt = 0;

export function openOfficeLockDialog() {
  listeners.forEach((fn) => fn());
}

/**
 * KAN-827 — for a redirect: the route that bounced the user (a deep link to
 * /log-job while SUSPENDED) unmounts before the destination's host exists, so a
 * plain open would reach nobody. With no host listening yet the request is held
 * (5s) and delivered to the FIRST host that mounts; with a host already there it
 * opens at once.
 */
const PENDING_OPEN_TTL_MS = 5000;
let pendingOpenAt = 0;
let pendingOpenTenant = null;
/** @param {string|null} [tenantId] the tenant the request was made under; a held request is dropped if the host mounts under another. */
export function openOfficeLockDialogWhenReady(tenantId = null) {
  if (listeners.size > 0) { openOfficeLockDialog(); return; }
  pendingOpenAt = Date.now();
  pendingOpenTenant = tenantId;
}

/** Sign-out / tenant switch: a held request must never reach the next session's host. */
export function clearPendingLockOpen() {
  pendingOpenAt = 0;
  pendingOpenTenant = null;
}

/**
 * Sender side of the form -> lock-dialog hand-off (modalHandoff.js recipe).
 * Call it from the SAME handler that stops rendering the form (hard-unmount, no
 * exit beat — never `requestClose`): it measures the form's panel, stages the
 * rect, and opens the dialog through the bus, so both state flips land in one
 * commit and the dialog FLIP-morphs from the form with the scrim already full.
 * A full-screen form passes its frame element (the frame shrinks into the
 * dialog). No panel / unmeasurable -> nothing staged, the dialog opens with
 * its normal enter beat.
 *
 *   handOffToCompanyLockDialog(dialogElRef.current);   // AnimatedModalShell dialogRef
 *   setFormOpen(false);                                // hard-unmount the form
 */
export function handOffToCompanyLockDialog(senderPanelEl) {
  stageModalHandoff(COMPANY_LOCK_HANDOFF_KEY, senderPanelEl);
  openOfficeLockDialog();
}

export function onOfficeLockDialogOpen(fn, tenantId = null) {
  listeners.add(fn);
  const sameTenant = !pendingOpenTenant || !tenantId || pendingOpenTenant === tenantId;
  const fresh = pendingOpenAt && Date.now() - pendingOpenAt < PENDING_OPEN_TTL_MS && sameTenant;
  clearPendingLockOpen();
  if (fresh) fn();
  return () => { listeners.delete(fn); };
}

/**
 * "Open Plan & billing" — the in-form lock notice's action AND the read-only
 * dialog's button. The ONE listener is the app-level `OpenPlanBillingHandler`
 * (mounted by ProtectedRoute for owner + admins): it closes what is on top,
 * then navigates (utils/company/openPlanBilling.js). Nobody listening (any
 * other role) -> harmless no-op.
 */
const planListeners = new Set();
export function requestOpenPlanBilling() {
  planListeners.forEach((fn) => fn());
}

export function onOpenPlanBillingRequest(fn) {
  planListeners.add(fn);
  return () => { planListeners.delete(fn); };
}

export function onCompanyLockRefusal(fn) {
  refusalListeners.add(fn);
  return () => { refusalListeners.delete(fn); };
}

export function reportCompanyLockRefusal() {
  const now = Date.now();
  if (lastRefusalAt && now - lastRefusalAt < LOCK_REFUSAL_DEBOUNCE_MS) return;
  lastRefusalAt = now;
  refusalListeners.forEach((fn) => { try { fn(); } catch { /* listener bug */ } });
}

export function __resetLockRefusalDebounceForTests() {
  lastRefusalAt = 0;
  clearPendingLockOpen();
}
